import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, Wrench, User } from 'lucide-react';
import Logo from './Logo';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop Parts' },
  { to: '/brands', label: 'Brands' },
  { to: '/shop', label: 'Categories' },
  { to: '/about', label: 'About Us' },
  { to: '/contact', label: 'Contact Us' },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    nav(`/shop?search=${encodeURIComponent(q)}`);
    setMobile(false);
  };

  return (
    <>
      <div data-testid="announcement-bar" className="bg-charcoal text-cream/80 font-mono text-[10px] sm:text-[11px] uppercase tracking-widest2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <span className="truncate">
            <span className="text-flame">●</span> Quality Tractor Parts. Reliable Service. Fast Shipping.
          </span>
          <span className="hidden md:block shrink-0">
            {user ? `Welcome back, ${user.name.split(' ')[0]}` : 'Free US shipping on orders over $99'}
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-forest/95 backdrop-blur border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-[72px] flex items-center gap-4 lg:gap-8">
            <button data-testid="mobile-menu-toggle" className="lg:hidden text-cream p-2 -ml-2" onClick={() => setMobile(!mobile)} aria-label="Menu">
              {mobile ? <X /> : <Menu />}
            </button>
            <Logo />
            <nav className="hidden lg:flex items-center gap-6 text-[13px] font-medium text-cream/80">
              {NAV.map((n, i) => (
                <Link key={i} to={n.to} className="hover:text-flame transition-colors duration-200 whitespace-nowrap">
                  {n.label}
                </Link>
              ))}
              {user?.role === 'admin' && (
                <Link to="/admin" data-testid="admin-nav-link" className="text-flame hover:text-amber2 transition-colors duration-200 whitespace-nowrap">
                  Admin
                </Link>
              )}
            </nav>
            <form onSubmit={submit} className="flex-1 max-w-md ml-auto hidden sm:flex relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search parts, SKU or model…"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-flame/60 focus:bg-white/8 transition"
              />
            </form>
            <div className="flex items-center gap-1 sm:gap-2 ml-auto sm:ml-0 shrink-0">
              <Link
                to={user ? '/account' : '/login'}
                data-testid="account-button"
                aria-label={user ? 'My account' : 'Sign in'}
                title={user ? user.name : 'Sign in'}
                className="hidden md:grid place-items-center w-10 h-10 rounded-full text-cream/70 hover:text-flame hover:bg-white/5 transition"
              >
                {user ? (
                  <span data-testid="header-user-initial" className="grid place-items-center w-7 h-7 rounded-full bg-flame text-white font-display font-bold text-sm uppercase">
                    {user.name.charAt(0)}
                  </span>
                ) : (
                  <User className="w-5 h-5" />
                )}
              </Link>
              <button
                data-testid="cart-drawer-toggle"
                aria-label="Cart"
                onClick={() => setOpen(true)}
                className="relative grid place-items-center w-10 h-10 rounded-full text-cream/80 hover:text-flame hover:bg-white/5 transition"
              >
                <ShoppingCart className="w-5 h-5" />
                {count > 0 && (
                  <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-flame text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full grid place-items-center px-1">
                    {count}
                  </span>
                )}
              </button>
              <Link
                to="/find-parts"
                data-testid="find-tractor-parts-header-button"
                className="hidden xl:inline-flex items-center gap-2 bg-flame hover:bg-ember text-white font-display font-semibold uppercase tracking-wide text-sm px-5 py-2.5 rounded-full transition-colors duration-200"
              >
                <Wrench className="w-4 h-4" /> Find Your Tractor Parts
              </Link>
            </div>
          </div>
        </div>
        {mobile && (
          <div className="lg:hidden border-t border-white/5 bg-forest px-4 py-4 space-y-3">
            <form onSubmit={submit} className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40" />
              <input
                data-testid="mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search parts, SKU or model…"
                className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-flame/60"
              />
            </form>
            {[...NAV, { to: user ? '/account' : '/login', label: user ? 'My Account' : 'Sign In' }, { to: '/find-parts', label: 'Find Your Tractor Parts' }].map((n, i) => (
              <Link
                key={i}
                to={n.to}
                onClick={() => setMobile(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, '-')}`}
                className="block font-display text-xl uppercase font-semibold text-cream/90 hover:text-flame transition"
              >
                {n.label}
              </Link>
            ))}
          </div>
        )}
      </header>
    </>
  );
}
